// src/frontend/src/pages/Dashboard.tsx - Enhanced with smart upgrade prompting
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { conversationService, scenarioService } from '../api/simulationApi';
import { Conversation, ConversationStatus, SimulationScenario } from '../types/simulation';
import CreditsMeter from '../components/Common/CreditsMeter';
import UpgradeBanner from '../components/Common/UpgradeBanner';
import ExitIntentUpgrade from '../components/Common/ExitIntentUpgrade';
import api from '../api/api';

// Smart upgrade prompting logic
const UPGRADE_PROMPT_STORAGE_KEY = 'pmcoach_upgrade_prompt_dismissed';

interface UpgradePromptState {
  dismissedAt: number;
  dismissCount: number;
  lastShownAt: number;
}

const getUpgradePromptState = (): UpgradePromptState => {
  const stored = localStorage.getItem(UPGRADE_PROMPT_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // If parsing fails, return default
    }
  }
  return {
    dismissedAt: 0,
    dismissCount: 0,
    lastShownAt: 0
  };
};

const setUpgradePromptState = (state: UpgradePromptState) => {
  localStorage.setItem(UPGRADE_PROMPT_STORAGE_KEY, JSON.stringify(state));
};

const shouldShowUpgradePrompt = (userPlan: any): boolean => {
  if (!userPlan?.isTrialActive) return false;
  
  const state = getUpgradePromptState();
  const now = Date.now();
  
  // If dismissed recently, respect different cooldown periods based on urgency
  if (state.dismissedAt > 0) {
    const timeSinceDismissed = now - state.dismissedAt;
    const trialDaysLeft = userPlan.trialDaysLeft || 0;
    
    // Very urgent (1-2 days left): Show again after 4 hours
    if (trialDaysLeft <= 2) {
      return timeSinceDismissed > (4 * 60 * 60 * 1000); // 4 hours
    }
    
    // Urgent (3-5 days left): Show again after 24 hours
    if (trialDaysLeft <= 5) {
      return timeSinceDismissed > (24 * 60 * 60 * 1000); // 24 hours
    }
    
    // Regular trial (6+ days left): Show again after 3 days
    return timeSinceDismissed > (3 * 24 * 60 * 60 * 1000); // 3 days
  }
  
  // If never shown before, or last shown was a while ago
  const timeSinceLastShown = now - state.lastShownAt;
  const minTimeBetweenShows = 24 * 60 * 60 * 1000; // 24 hours minimum
  
  return timeSinceLastShown > minTimeBetweenShows;
};

const Dashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [recentSessions, setRecentSessions] = useState<Conversation[]>([]);
  const [featuredScenarios, setFeaturedScenarios] = useState<SimulationScenario[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [isLoadingScenarios, setIsLoadingScenarios] = useState(true);
  const [error, setError] = useState<string>('');
  const [userPlan, setUserPlan] = useState<any>(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showPaymentCancelled, setShowPaymentCancelled] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [currentTip, setCurrentTip] = useState(0);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false); // Smart state
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  
  const quickStartTips = [
    "Start with easier scenarios to build confidence, then progress to harder ones",
    "Use real-time coaching tips to improve your responses during conversations", 
    "Review your feedback reports to identify patterns in your communication style",
    "Try different approaches to the same scenario to see how stakeholders react"
  ];

  useEffect(() => {
    // Handle payment redirects from Stripe
    const payment = searchParams.get('payment');
    
    if (payment === 'success') {
      setShowPaymentSuccess(true);
      setSearchParams({});
      setTimeout(() => setShowPaymentSuccess(false), 10000);
    } else if (payment === 'cancelled') {
      setShowPaymentCancelled(true);
      setSearchParams({});
      setTimeout(() => setShowPaymentCancelled(false), 10000);
    }

    // Check for welcome message flag
    if (sessionStorage.getItem('showWelcome')) {
      setShowWelcomeMessage(true);
      sessionStorage.removeItem('showWelcome');
      setTimeout(() => setShowWelcomeMessage(false), 15000);
    }

    fetchRecentSessions();
    fetchFeaturedScenarios();
    fetchUserPlan();
  }, [searchParams, setSearchParams]);

  // Check if we should show upgrade prompt after user plan is loaded
  useEffect(() => {
    if (userPlan) {
      const shouldShow = shouldShowUpgradePrompt(userPlan);
      setShowUpgradePrompt(shouldShow);
      
      // Track that we've shown it
      if (shouldShow) {
        const state = getUpgradePromptState();
        setUpgradePromptState({
          ...state,
          lastShownAt: Date.now()
        });
      }
    }
  }, [userPlan]);

  // Rotating tips effect
  useEffect(() => {
    const tipInterval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % quickStartTips.length);
    }, 5000);
    return () => clearInterval(tipInterval);
  }, [quickStartTips.length]);

  const fetchRecentSessions = async () => {
    try {
      setIsLoadingSessions(true);
      const response = await conversationService.getConversations();
      setRecentSessions(response.data.data.slice(0, 5));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch recent coaching sessions');
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const fetchFeaturedScenarios = async () => {
    try {
      setIsLoadingScenarios(true);
      const response = await scenarioService.getScenarios();
      setFeaturedScenarios(response.data.data.slice(0, 4));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch featured scenarios');
    } finally {
      setIsLoadingScenarios(false);
    }
  };

  const fetchUserPlan = async () => {
    try {
      const response = await api.get('/users/plan');
      setUserPlan(response.data.data);
    } catch (err) {
      console.error('Failed to fetch user plan:', err);
    }
  };

  const createCheckoutSession = async (planType: string, billingPeriod: string) => {
    setIsCreatingCheckout(true);
    try {
      const response = await api.post('/payments/create-checkout-session', {
        planType,
        billingPeriod
      });
      
      if (response.data.data.url) {
        window.location.href = response.data.data.url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert('Failed to start upgrade. Please try again.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  // Enhanced dismiss handler with smart tracking
  const handleUpgradePromptDismiss = (action: 'decide_later' | 'not_interested') => {
    const state = getUpgradePromptState();
    const now = Date.now();
    
    setUpgradePromptState({
      dismissedAt: now,
      dismissCount: state.dismissCount + 1,
      lastShownAt: state.lastShownAt
    });
    
    setShowUpgradePrompt(false);
    
    // Optional: Track analytics
    console.log(`Upgrade prompt dismissed: ${action}, count: ${state.dismissCount + 1}`);
  };

  // Quick access to upgrade for users who want it
  const handleQuickUpgrade = () => {
    navigate('/pricing?source=dashboard_quick');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusColor = (status: ConversationStatus) => {
    switch (status) {
      case ConversationStatus.IN_PROGRESS:
        return 'bg-yellow-100 text-yellow-800';
      case ConversationStatus.COMPLETED:
        return 'bg-green-100 text-green-800';
      case ConversationStatus.ABANDONED:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: ConversationStatus) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const formatEnumValue = (value: string): string => {
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score: number) => {
    if (score >= 8) return 'bg-green-50 border-green-200';
    if (score >= 6) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  // Calculate progress metrics
  const avgScore = recentSessions.length > 0 
    ? recentSessions
        .filter(session => session.feedback?.overallScore)
        .reduce((sum, session) => sum + (session.feedback?.overallScore || 0), 0) / 
      recentSessions.filter(session => session.feedback?.overallScore).length
    : 0;

  const completedSessions = recentSessions.filter(session => session.status === ConversationStatus.COMPLETED).length;

  // Calculate weekly goal progress
  const thisWeekStart = new Date();
  thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
  thisWeekStart.setHours(0, 0, 0, 0);
  
  const thisWeekSessions = recentSessions.filter(session => 
    new Date(session.startedAt) >= thisWeekStart
  ).length;
  
  const weeklyGoalTarget = 3;
  const weeklyProgress = Math.min(thisWeekSessions / weeklyGoalTarget, 1) * 100;

  // Calculate streak
  const calculateStreak = () => {
    if (recentSessions.length === 0) return 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let streak = 0;
    let currentDate = new Date(today);
    
    while (streak < 30) {
      const hasSessionOnDate = recentSessions.some(session => {
        const sessionDate = new Date(session.startedAt);
        sessionDate.setHours(0, 0, 0, 0);
        return sessionDate.getTime() === currentDate.getTime();
      });
      
      if (hasSessionOnDate) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }
    
    return streak;
  };

  const currentStreak = calculateStreak();

  // Calculate recent achievements
  const getRecentAchievements = () => {
    const achievements = [];
    
    const perfectScoreSessions = recentSessions.filter(session => 
      session.feedback?.overallScore === 10
    );
    if (perfectScoreSessions.length > 0) {
      achievements.push({
        id: 'perfect-score',
        title: 'Perfect Score!',
        icon: '🎯',
        date: formatDate(perfectScoreSessions[0].startedAt)
      });
    }
    
    if (currentStreak >= 3) {
      achievements.push({
        id: 'streak',
        title: `${currentStreak} Day Streak`,
        icon: '🔥',
        date: 'Active now'
      });
    }
    
    if (avgScore >= 8) {
      achievements.push({
        id: 'high-performer',
        title: 'High Performer',
        icon: '⭐',
        date: 'This month'
      });
    }
    
    if (completedSessions >= 5) {
      achievements.push({
        id: 'milestone',
        title: `${completedSessions} Sessions Completed`,
        icon: '🏆',
        date: 'This month'
      });
    }
    
    return achievements.slice(0, 3);
  };

  const recentAchievements = getRecentAchievements();

  return (
    <Layout>
      <div className="flex flex-col space-y-8">
        {/* Exit Intent Upgrade Modal */}
        <ExitIntentUpgrade />

        {/* Payment Success Message */}
        {showPaymentSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium">Payment successful!</p>
                <p className="text-sm">Your subscription has been activated. You now have full access to all coaching features.</p>
              </div>
            </div>
            <button
              onClick={() => setShowPaymentSuccess(false)}
              className="text-green-600 hover:text-green-800"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {/* Payment Cancelled Message */}
        {showPaymentCancelled && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-4 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium">Payment cancelled</p>
                <p className="text-sm">Your subscription upgrade was cancelled. You can try again anytime.</p>
              </div>
            </div>
            <button
              onClick={() => setShowPaymentCancelled(false)}
              className="text-yellow-600 hover:text-yellow-800"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {/* Enhanced Welcome Message */}
        {showWelcomeMessage && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Welcome to PM Coach! 🎯</h2>
                  <p className="text-blue-100 mb-4">
                    Your 14-day free coaching trial has started. Check your email for getting started tips!
                  </p>
                  <Link
                    to="/learning-tracks"
                    className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-md font-medium hover:bg-gray-50 transition-colors"
                  >
                    Start Your First Learning Track
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                </div>
                <button
                  onClick={() => setShowWelcomeMessage(false)}
                  className="text-white/70 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ENHANCED: Smart Upgrade Prompt - Only shows when appropriate */}
        {userPlan && userPlan.isTrialActive && userPlan.trialDaysLeft > 0 && showUpgradePrompt && (
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6 mb-6 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-purple-100 rounded-full -mr-12 -mt-12 opacity-50"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-purple-900">
                      {userPlan.trialDaysLeft <= 3 ? 'Trial Ending Soon!' : 'Choose Your Plan'}
                    </h3>
                    <p className="text-purple-700 text-sm">
                      {userPlan.trialDaysLeft <= 3 
                        ? `Don't lose access to your PM coaching progress`
                        : 'Secure your continued access to PM coaching'
                      }
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-600 mb-1">
                    {userPlan.trialDaysLeft}
                  </div>
                  <div className="text-xs text-purple-600">days left</div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Solo Core - What they're currently trying */}
                <div className="bg-white/60 rounded-lg p-4 border-2 border-gray-300">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">Continue with Solo Core</h4>
                    <span className="text-sm bg-gray-100 text-gray-600 px-2 py-1 rounded-full">What you're trying</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-2">$19<span className="text-base font-normal text-gray-600">/month</span></div>
                  <ul className="text-sm text-gray-700 space-y-1 mb-4">
                    <li>• 20 sessions/month</li>
                    <li>• All persona packs</li>
                    <li>• PDF feedback reports</li>
                    <li>• Email support</li>
                    <li>• {userPlan.creditsTotal - userPlan.creditsUsed} sessions remaining this trial</li>
                  </ul>
                  <button
                    onClick={() => createCheckoutSession('solo_core', 'monthly')}
                    disabled={isCreatingCheckout}
                    className="w-full bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50"
                  >
                    {isCreatingCheckout ? 'Setting up...' : 'Subscribe to Solo Core'}
                  </button>
                </div>

                {/* Solo Pro - The upgrade option */}
                <div className="bg-white/60 rounded-lg p-4 border-2 border-blue-300 relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular Upgrade
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-blue-900">Upgrade to Solo Pro</h4>
                    <span className="text-sm bg-blue-100 text-blue-600 px-2 py-1 rounded-full">3x more!</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600 mb-2">$39<span className="text-base font-normal text-gray-600">/month</span></div>
                  <ul className="text-sm text-gray-700 space-y-1 mb-4">
                    <li>• <strong>60 sessions/month (3x more!)</strong></li>
                    <li>• <strong>Real-time coaching tips</strong></li>
                    <li>• Custom scenario creator</li>
                    <li>• Priority email support</li>
                    <li>• Everything in Solo Core</li>
                  </ul>
                  <button
                    onClick={() => createCheckoutSession('solo_pro', 'monthly')}
                    disabled={isCreatingCheckout}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {isCreatingCheckout ? 'Setting up...' : 'Upgrade to Solo Pro'}
                  </button>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="text-sm text-purple-700">
                  <strong>Your progress will be saved</strong> with either plan
                </div>
                <div className="flex gap-2">
                  {/* Quick upgrade access */}
                  <button
                    onClick={handleQuickUpgrade}
                    className="px-3 py-2 text-purple-700 hover:text-purple-900 text-sm underline"
                  >
                    Compare All Plans
                  </button>
                  <button
                    onClick={() => handleUpgradePromptDismiss('decide_later')}
                    className="px-4 py-2 border border-purple-300 rounded-lg text-purple-700 hover:bg-purple-50 transition-colors text-sm"
                  >
                    {userPlan.trialDaysLeft <= 3 ? 'Remind Me Tomorrow' : 'Decide Later'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Banner - Standard upgrade prompts */}
        {userPlan && (
          <UpgradeBanner
            planType={userPlan.planType}
            creditsPercentageUsed={(userPlan.creditsUsed / userPlan.creditsTotal) * 100}
            trialDaysLeft={userPlan.trialDaysLeft}
          />
        )}
        
        {/* Layout with enhanced sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-6">

            {/* Enhanced welcome section with stats */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold mb-2">Your PM Coaching Dashboard</h1>
                  <p className="text-gray-600">
                    Master stakeholder conversations with AI-powered coaching designed specifically for PMs. 
                    Start with <strong>Learning Tracks</strong> for guided progression or explore our full scenario library.
                  </p>
                </div>
                
                {/* Quick Upgrade Button in Header - Always available but not pushy */}
                {userPlan && (userPlan.isTrialActive || userPlan.planType === 'solo_core') && (
                  <div className="flex flex-col items-end">
                    <button
                      onClick={() => navigate('/pricing')}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium flex items-center shadow-lg mb-2"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      {userPlan.planType === 'solo_core' ? 'Upgrade to Pro' : 'Upgrade Now'}
                    </button>
                    {userPlan.isTrialActive && (
                      <div className="text-right text-xs text-gray-500">
                        Trial: {userPlan.trialDaysLeft} days left
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Link
                  to="/learning-tracks"
                  className="group relative bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 transition-all duration-200 hover:shadow-md"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-500 text-white group-hover:bg-blue-600 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h2 className="text-lg font-semibold group-hover:text-blue-700">Learning Tracks</h2>
                      <p className="text-sm text-gray-600">Structured career-focused paths</p>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 text-blue-400 group-hover:text-blue-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>     
                
                <Link
                  to="/coaching-sessions"
                  className="group relative bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 hover:from-purple-100 hover:to-pink-100 hover:border-purple-300 transition-all duration-200 hover:shadow-md"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-500 text-white group-hover:bg-purple-600 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h2 className="text-lg font-semibold group-hover:text-purple-700">Browse Scenarios</h2>
                      <p className="text-sm text-gray-600">Explore our full scenario library</p>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 text-purple-400 group-hover:text-purple-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
                
                <Link
                  to="/coaching-history"
                  className="group relative bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 hover:from-green-100 hover:to-emerald-100 hover:border-green-300 transition-all duration-200 hover:shadow-md"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-500 text-white group-hover:bg-green-600 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h2 className="text-lg font-semibold group-hover:text-green-700">View Progress</h2>
                      <p className="text-sm text-gray-600">Track your coaching journey</p>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 text-green-400 group-hover:text-green-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </div>

              {/* Progress Overview */}
              {!isLoadingSessions && recentSessions.length > 0 && (
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Your Progress</h3>
                    <span className="text-sm text-gray-500">This month</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-1">{completedSessions}</div>
                      <div className="text-sm text-gray-600">Sessions Completed</div>
                      <div className="text-xs text-gray-500 mt-1">Keep it up! 💪</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600 mb-1">
                        {avgScore > 0 ? avgScore.toFixed(1) : "0.0"}
                      </div>
                      <div className="text-sm text-gray-600">Average Score</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {avgScore > 0 ? "Great improvement! 📈" : "Start your first session"}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600 mb-1">
                        {userPlan ? userPlan.creditsTotal - userPlan.creditsUsed : "—"}
                      </div>
                      <div className="text-sm text-gray-600">Sessions Remaining</div>
                      <div className="text-xs text-gray-500 mt-1">Ready when you are! ⚡</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Sessions section */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Recent Coaching Sessions</h2>
                <Link to="/coaching-history" className="text-blue-600 hover:text-blue-800 text-sm">
                  View All →
                </Link>
              </div>
              
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              
              {isLoadingSessions ? (
                <div className="text-center py-4">
                  <p>Loading recent coaching sessions...</p>
                </div>
              ) : recentSessions.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to start coaching?</h3>
                  <p className="text-gray-500 mb-4">
                    New to PM Coach? Start with Learning Tracks for a guided experience, or jump into any scenario that interests you.
                  </p>
                  <div className="flex justify-center gap-3">
                    <Link to="/learning-tracks" className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      Start Learning Tracks
                    </Link>
                    <Link to="/coaching-sessions" className="inline-block px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                      Browse Scenarios
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentSessions.map((conversation) => {
                    const scenario = typeof conversation.scenario === 'object' ? conversation.scenario : null;
                    const stakeholder = typeof conversation.stakeholderPersona === 'object' ? conversation.stakeholderPersona : null;
                    
                    return (
                      <Link 
                        key={conversation._id}
                        to={`/coaching-sessions/${conversation._id}`}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors block"
                      >
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{scenario ? scenario.title : 'Unknown Scenario'}</h3>
                          <p className="text-sm text-gray-600">with {stakeholder ? stakeholder.name : 'Unknown Stakeholder'}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(conversation.startedAt)}</p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(conversation.status)}`}>
                            {formatStatus(conversation.status)}
                          </span>
                          {conversation.status === ConversationStatus.COMPLETED && conversation.feedback && (
                            <div className={`px-3 py-1 rounded-full border ${getScoreBackground(conversation.feedback.overallScore)}`}>
                              <span className={`text-sm font-medium ${getScoreColor(conversation.feedback.overallScore)}`}>
                                {conversation.feedback.overallScore}/10
                              </span>
                            </div>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Featured Scenarios section */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Featured Coaching Scenarios</h2>
                <Link to="/coaching-sessions" className="text-blue-600 hover:text-blue-800 text-sm">
                  View All →
                </Link>
              </div>
              
              {isLoadingScenarios ? (
                <div className="text-center py-4">
                  <p>Loading scenarios...</p>
                </div>
              ) : featuredScenarios.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No scenarios available.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {featuredScenarios.map((scenario) => (
                    <div key={scenario._id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-medium">{scenario.title}</h3>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          scenario.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                          scenario.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {formatEnumValue(scenario.difficulty)}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{scenario.description}</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          {formatEnumValue(scenario.category)}
                        </span>
                        <span className="inline-block px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                          {formatEnumValue(scenario.stakeholderType)}
                        </span>
                      </div>
                      <Link
                        to={`/coaching-sessions`}
                        className="block text-center w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Start This Scenario
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Enhanced Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Credits Meter with Enhanced Upgrade */}
            {userPlan && (
              <>
                <CreditsMeter
                  creditsUsed={userPlan.creditsUsed}
                  creditsTotal={userPlan.creditsTotal}
                  planType={userPlan.planType}
                  trialDaysLeft={userPlan.trialDaysLeft}
                />
                
                {/* Trial warning if applicable */}
                {userPlan.isTrialActive && userPlan.trialDaysLeft <= 3 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-yellow-800">
                      Trial ends in {userPlan.trialDaysLeft} days
                    </p>
                    <Link
                      to="/pricing"
                      className="text-sm text-yellow-700 hover:text-yellow-900 underline"
                    >
                      Subscribe now →
                    </Link>
                  </div>
                )}
              </>
            )}

            {/* Weekly Goal Tracker */}
            {recentSessions.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Weekly Goal</h3>
                  <span className="text-xs text-blue-600 font-medium">
                    {thisWeekSessions}/{weeklyGoalTarget} sessions
                  </span>
                </div>
                <div className="mb-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${weeklyProgress}%` }}
                    ></div>
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  {thisWeekSessions >= weeklyGoalTarget 
                    ? "🎉 Weekly goal achieved!" 
                    : `${weeklyGoalTarget - thisWeekSessions} more to reach your weekly goal! 🎯`
                  }
                </p>
              </div>
            )}

            {/* Current Streak */}
            {currentStreak > 0 && (
              <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white text-lg">🔥</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-orange-800">Current Streak</h3>
                    <p className="text-xs text-orange-600">{currentStreak} day{currentStreak !== 1 ? 's' : ''} in a row</p>
                  </div>
                </div>
                <p className="text-xs text-orange-700">
                  {currentStreak >= 7 
                    ? "Amazing dedication! 🏆" 
                    : "Keep practicing to maintain your streak!"
                  }
                </p>
              </div>
            )}

            {/* Rotating Quick Tips */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-yellow-800">Quick Tip</h3>
              </div>
              <p className="text-sm text-yellow-700 leading-relaxed">
                {quickStartTips[currentTip]}
              </p>
              <div className="flex mt-3 space-x-1">
                {quickStartTips.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentTip ? 'bg-yellow-500' : 'bg-yellow-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Recent Achievements */}
            {recentAchievements.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Achievements</h3>
                <div className="space-y-3">
                  {recentAchievements.map((achievement) => (
                    <div key={achievement.id} className="flex items-center">
                      <span className="text-lg mr-3">{achievement.icon}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{achievement.title}</p>
                        <p className="text-xs text-gray-500">{achievement.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Challenge CTA */}
            {recentSessions.length > 0 && avgScore >= 6 && (
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-4 text-white">
                <h3 className="text-sm font-semibold mb-2">Ready for a Challenge?</h3>
                <p className="text-xs text-blue-100 mb-3">
                  {avgScore >= 8 
                    ? "You're performing great! Try a hard scenario to push your skills even further."
                    : "Try a harder scenario to challenge yourself and improve your scores."
                  }
                </p>
                <Link 
                  to="/coaching-sessions"
                  className="block w-full bg-white text-blue-600 py-2 px-3 rounded text-sm font-medium hover:bg-gray-100 transition-colors text-center"
                >
                  Find Hard Scenarios
                </Link>
              </div>
            )}

            {/* Quick Links */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Links</h3>
              <div className="space-y-2">
                <Link to="/pricing" className="flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors">
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Upgrade Plan
                </Link>
                <Link to="/settings" className="flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors">
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Account Settings
                </Link>
                <a href="mailto:support@pmcoach.app" className="flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors">
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Get Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;